import { Link } from 'react-router-dom'
import { AppWindow, KeyRound, Mail, Radio, Video } from 'lucide-react'
import { ButtonLink, Card } from '@/components/play'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { SitePage } from '@/components/SitePage'

const sections = [
  {
    title: 'Using StreamBridge',
    body: [
      'You need to be old enough to accept these terms where you live, or have a parent or guardian accept them for you.',
      'If you use StreamBridge for a club, company, or other organization, you accept these terms for it and confirm you’re allowed to.',
      'Keep your account, stream keys, camera invites, and access tokens to yourself. You’re responsible for what’s done with them.',
      'Hosts are responsible for the permissions their event and cameras need, from the race and from the people on camera.',
    ],
  },
  {
    title: 'Camera streams stay on StreamBridge',
    body: [
      'Cameras bring hosts and events the content they need to make a great show. That’s what camera streams are for.',
      'Each camera’s stream plays only in StreamBridge: on the event page, in the event workspace, and in our apps.',
      'The one use outside StreamBridge is as a source for a produced live show. How the show is produced is up to you: OBS, a vision mixer, or any other tool. Replays within the show count as part of it.',
      'You may not show, embed, restream, rebroadcast, record to publish, or sell an individual camera stream anywhere else, on any site, app, player, or platform.',
      'A live show is a programme you produce from your sources. A single camera passed through unchanged isn’t a live show.',
    ],
  },
  {
    title: 'Your live show',
    body: [
      'You can stream a live show made from your event’s cameras to other platforms, such as YouTube or Facebook, as well as on StreamBridge.',
      'You’re responsible for the permissions your show needs on those platforms, such as for music and sponsors.',
    ],
  },
  {
    title: 'Embedding StreamBridge',
    body: [
      'You can share links to StreamBridge event pages anywhere.',
      'Embedding StreamBridge in another site or app, including event pages, cameras, maps, and chat, needs our written permission first.',
      'With permission, embed the event page as we provide it, on the sites we’ve approved, with StreamBridge branding and links in place.',
      'We can withdraw permission to embed at any time.',
    ],
  },
  {
    title: 'Tokens, the API, and our systems',
    body: [
      'Our public endpoints and viewer tokens exist to run StreamBridge’s own pages and apps. Being able to reach them isn’t permission to use them for anything else.',
      'Don’t request viewer tokens or other access from StreamBridge for other people, pass them on, or connect to our video rooms and media servers except through StreamBridge pages and apps.',
      'Don’t scrape or harvest StreamBridge data, such as camera lists, locations, and chat, or get around rate limits, access controls, or other protections.',
      'Use personal access tokens, OAuth, and the MCP server only for your own organization’s events, within the limits we set.',
    ],
  },
  {
    title: 'Your content',
    body: [
      'You keep ownership of the video, sound, and other content you bring to StreamBridge.',
      'You give us permission to carry, process, and show it as needed to run your events and StreamBridge, including on the public event page.',
      'You confirm you have the rights to that content and that it doesn’t break the law or anyone else’s rights.',
    ],
  },
  {
    title: 'What’s not allowed',
    body: [
      'Don’t use StreamBridge to break the law, harass people, or share content you don’t have the rights to.',
      'Don’t disrupt or overload StreamBridge, upload malware, or pretend to be someone else.',
    ],
  },
  {
    title: 'If these terms are broken',
    body: [
      'We can block access, revoke tokens and keys, withdraw permission to embed, and suspend or close accounts.',
      'We can also take legal action and, where the law allows, recover what the breach costs us, such as the video delivery it caused.',
    ],
  },
  {
    title: 'The service',
    body: [
      'Live video depends on networks, devices, and providers we don’t control, so we can’t promise every camera or show stays up.',
      'As far as the law allows, we aren’t liable for indirect or consequential losses, or for losses caused by networks, devices, or services we don’t control.',
      'We may change StreamBridge and these terms. When we change the terms, we update the date at the top and tell account holders about significant changes. Using StreamBridge after a change means you accept it.',
    ],
  },
  {
    title: 'Law',
    body: [
      'These terms are governed by the laws of the State of Delaware, and the state and federal courts in Delaware can hear any dispute about them.',
    ],
  },
]

const highlights = [
  { icon: Video, label: 'Camera streams play only on StreamBridge, or as sources for a produced live show.' },
  { icon: Radio, label: 'Your live show can stream to any platform.' },
  { icon: AppWindow, label: 'Embedding StreamBridge needs our written permission.' },
  { icon: KeyRound, label: 'Tokens, keys, and invites aren’t for passing on.' },
]

/** Terms (/terms): how StreamBridge may be used, what stays on the platform and what a live show may go out to. */
export function TermsPage() {
  useDocumentTitle('Terms · StreamBridge')
  return (
    <SitePage mainId="terms-main" className="text-neutral-10" mainClassName="px-6 py-16">
      <div className="mx-auto max-w-4xl">
        <div className="mb-12">
          <p className="mb-4 text-size-xs font-medium text-neutral-7">Terms of service</p>
          <h1 className="text-4xl font-black sm:text-5xl">
            Cameras stay here. Your show goes anywhere.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-neutral-7">
            These terms cover how you use StreamBridge: the website, event pages, camera apps, and API. By using
            StreamBridge you accept them.
          </p>
          <p className="mt-4 max-w-2xl text-neutral-7">
            These terms are an agreement between you and StreamBridge, a Delaware corporation. Our{' '}
            <Link to="/privacy" className="text-neutral-10 underline underline-offset-4 hover:text-neutral-8">
              privacy policy
            </Link>{' '}
            explains how we handle personal information.
          </p>
          <p className="mt-4 text-sm text-neutral-7">Last updated <time dateTime="2026-10-24">October 24, 2026</time></p>
        </div>

        {/* role="list" because VoiceOver drops list semantics from an unstyled list. */}
        <ul className="mb-12 grid gap-4 sm:grid-cols-2" role="list" aria-label="In short">
          {highlights.map((item) => (
            <li key={item.label} className="flex gap-3 rounded-lg bg-neutral-2 p-4">
              <item.icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-neutral-10" />
              <p className="text-sm text-neutral-8">{item.label}</p>
            </li>
          ))}
        </ul>

        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="mb-4 text-2xl font-bold">{section.title}</h2>
              <ul className="space-y-3" role="list">
                {section.body.map((item) => (
                  <li key={item} className="flex gap-3 text-neutral-8">
                    <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-10" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <Card as="section" title="Contact us" description="Questions about these terms, or want permission to embed?" className="mt-12">
          <ButtonLink href="mailto:hello@streambridge.live">
            <Mail aria-hidden="true" />
            hello@streambridge.live
          </ButtonLink>
        </Card>
      </div>
    </SitePage>
  )
}
